/* IBM Confidential
*
* OCO Source Materials
*

* IBM Cognos Products: ba-ui-toolkit
* (C) Copyright IBM Corp. 2019
*
* The source code for this program is not published or otherwise
* divested of its trade secrets, irrespective of what has been
* deposited with the U.S. Copyright Office.
* NLS_ENCODING=UTF-8
* NLS_MESSAGEFORMAT_NONE
*/

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils, keyboardUtils } from '@ba-ui-toolkit/utils';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import BaseInput from '@ba-ui-toolkit/baseinput';
import chevronUp from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-up_16';
import chevronDown from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-down_16';
import styles from './NumberInput.scss';
import stringResource from './nls/StringResource';

const { isKey, keyNames } = keyboardUtils;
const { sanitize, getInheritedProps } = propsUtils;

const _CLASS = styles.numberInputClassName.replace(/"/g, '');
const DEFAULT_VALIDATION = new RegExp(/^[+-]{0,1}[0-9]*([.][0-9]*){0,1}$/);

export default class NumberInput extends Component {
	static propTypes = {
		...BaseInput.propTypes,

		// these props have a different definition then the baseInput props
		type: PropTypes.oneOf([]),

		// these props are in addition to the baseInput props
		max: PropTypes.number,
		ltMax: PropTypes.bool,
		gtMin: PropTypes.bool,
		min: PropTypes.number,
		step: PropTypes.number,
		scale: PropTypes.number,
		validation: PropTypes.func,
		allowEmpty: PropTypes.bool,
	};

	static defaultProps = {
		scale: 10,
		className: '',
		style: {},
		step: 1,
		allowEmpty: false,
	};

	constructor(props) {
		super(props);
		this.state = {
			displayValue: undefined,
		};
	}

	// eventually/hopefully onValueChange/onValueAccept will be the norm, and useOnValueCallbacks can be removed
	_useOnValueCallbacks = !!this.props.onValueChange || !!this.props.onValueAccept;

	// accept only a valid string as input
	_fixupValue = value => {
		if (value === undefined) {
			return undefined;
		}
		if (typeof value === 'number') {
			return String(value);
		}
		if (typeof value === 'string' && value.length) {
			// old validation callbacks allowed invalid values, new validations do not
			if (!this._useOnValueCallbacks) {
				return value;
			}

			return String(Number(value));
		}

		return '';
	};

	componentDidMount() {
		const { value } = this.props;

		const fixedNewValue = this._fixupValue(value);
		const { rawValue, infoMessage } = this._valueInfo(fixedNewValue);

		this.setState({
			displayValue: rawValue,
			infoMessage,
		});
	}

	componentWillReceiveProps(nextProps) {
		const { value: newValue } = nextProps;
		const { value: oldValue } = this.props;
		const fixedOldValue = this._fixupValue(oldValue);
		const fixedNewValue = this._fixupValue(newValue);
		const { rawValue, infoMessage } = this._valueInfo(fixedNewValue, nextProps);

		this.setState({ infoMessage });
		if (fixedNewValue !== fixedOldValue) {
			this.setState({ displayValue: rawValue });
		}
	}

	focus() {
		const { disabled } = this.props;

		!disabled && this._input && this._input.focus();
	}

	_ref = input => {
		this._input = input;
	};

	_constrainedValue(value) {
		const { min, max, scale, gtMin, ltMax } = this.props;
		if (!value || isNaN(value)) {
			return '';
		}
		const scaleValue = 10 ** scale; // ES6 syntax for Math.pow(10, scale);
		let number = Math.floor(Number(value) * scaleValue) / scaleValue;
		if (min != null) {
			const scaledMin = Math.floor(min * scaleValue) / scaleValue;
			number = Math.max(scaledMin, number);
			if (gtMin && number === scaledMin) {
				return String(number + 10 ** -scale);
			}
		}
		if (max != null) {
			const scaledMax = Math.floor(max * scaleValue) / scaleValue;
			number = Math.min(scaledMax, number);
			if (ltMax && number === scaledMax) {
				return String(number - 10 ** -scale);
			}
		}

		return String(number);
	}

	_onChangeWithUpdate = v => {
		const { validation } = this.props;
		let value = v;

		if (validation) {
			if (!validation(value)) {
				return; // ignore input if invalid
			}
			// check if custom validated value can be constrained
			value = isNaN(value) ? value : this._constrainedValue(value);
		} else {
			value = this._constrainedValue(value) || this.props.value;
		}
		this._updateValues(value);
	};

	_onBlur = e => {
		const { onBlur } = this.props;

		this._onChangeWithUpdate(e.currentTarget.value);
		onBlur && onBlur(e);
	};

	_onChange = v => {
		const { validation } = this.props;

		// Use custom validation function if provided by consumer
		if (validation && v !== '') {
			if (!validation(v)) {
				return; // ignore input if invalid
			}
			this._updateValues(v);
		} else if (DEFAULT_VALIDATION.test(v)) {
			this._updateValues(v);
		}
	};

	_updateValues = v => {
		const { onChange } = this.props;

		if (onChange) {
			onChange(v);
		} else {
			this.setState({ displayValue: v });
		}
	};

	_onKeyDown = e => {
		const { onKeyDown } = this.props;
		const { isDefaultPrevented } = e;
		const { UP, DOWN } = keyNames;

		if (!isDefaultPrevented()) {
			if (isKey(e, UP)) {
				this._spin(e, +1);
			} else if (isKey(e, DOWN)) {
				this._spin(e, -1);
			}
		}

		onKeyDown && onKeyDown(e);
	};

	_spin(e, stepDirection) {
		const { disabled, step } = this.props;
		const { displayValue } = this.state;
		const { rawValue, normalized } = this._valueInfo(displayValue);

		if (disabled || step <= 0) {
			return;
		}

		e.preventDefault();

		// if the input is not or can not be a number, we can't spin it
		if (normalized === undefined) {
			return;
		}

		this.focus();

		// TODO: need to account for rounding issues when dealing with decimal values
		// assumeing a scale = 3, step = 1.6, and the input = 1.9
		// ie: String(+'1.9' + (-1 * 1.6)) = 0.2999999999999998 (should be 0.3)
		// ie: String(+'-1.9' + (1 * 1.6)) = -0.2999999999999998 (should be -0.3)
		// ie: String(+'1.9' + (1 * 1.6)) = 3.5 (should be 3.5)
		// ie: String(+'-1.9' + (-1 * 1.6)) = -3.5 (should be -3.5)
		const incrementedValue = String(+rawValue + stepDirection * step);

		if (this._useOnValueCallbacks) {
			// uses baseInput's private function to change and accept callbacks
			this._input._onValueChange(incrementedValue);
			this._input._onValueAccept(incrementedValue);
		} else {
			this._onChangeWithUpdate(incrementedValue);
		}
	}

	_valueFixUpMin = (valueBounded, props) => {
		const { min, gtMin, step } = props;

		if (min !== undefined) {
			if (gtMin) {
				if (Number(valueBounded.boundValue) <= Number(min)) {
					valueBounded.boundValue = String(min + step);
					valueBounded.infoMessage = stringResource.get('minGT', { value: min });
				}
			} else if (Number(valueBounded.boundValue) < Number(min)) {
				valueBounded.boundValue = String(min);
				valueBounded.infoMessage = stringResource.get('min', { value: min });
			}
		}
	};

	_valueFixUpMax = (valueBounded, props) => {
		const { max, ltMax, step } = props;

		if (max !== undefined) {
			if (ltMax) {
				if (Number(valueBounded.boundValue) >= Number(max)) {
					valueBounded.boundValue = String(max - step);
					valueBounded.infoMessage = stringResource.get('maxLT', { value: max });
				}
			} else if (Number(valueBounded.boundValue) > Number(max)) {
				valueBounded.boundValue = String(max);
				valueBounded.infoMessage = stringResource.get('max', { value: max });
			}
		}
	};

	_valueFixUpScale = (valueBounded, value, props) => {
		const { scale } = props;

		if (scale !== undefined) {
			const valueParts = valueBounded.boundValue.split('.');

			// TODO: pad values to scale = add zeros
			if (valueParts.length === 2 && valueParts[1].length > scale) {
				if (scale === 0) {
					valueBounded.boundValue = valueParts[0];
					if (value.split('.').length !== 1) {
						valueBounded.infoMessage = stringResource.get('scale0');
					}
				} else {
					const boundedScalePart = valueParts[1].slice(0, scale);
					valueBounded.boundValue = `${valueParts[0]}.${boundedScalePart}`;
					if (valueParts[1].length > boundedScalePart.length) {
						valueBounded.infoMessage = stringResource.get('scale', {
							value: scale,
						});
					}
				}
			}
		}
	};

	_valueBounded = (value, normalized, props) => {
		const valueBounded = {
			boundValue: normalized,
		};

		this._valueFixUpMin(valueBounded, props);
		this._valueFixUpMax(valueBounded, props);
		// scale needs to be last
		this._valueFixUpScale(valueBounded, value, props);

		return valueBounded;
	};

	_valueInfo = (value, props) => {
		const { allowEmpty } = this.props;

		// if value is empty
		if (!value) {
			return {
				rawValue: value,
				sign: '',
				whole: '0',
				fraction: '0',
				normalized: '0.0',
				boundValue: allowEmpty ? value : undefined,
				infoMessage: allowEmpty ? undefined : stringResource.get('empty'),
			};
		}

		// check if value is a number

		// "-1.2".split(/([+-]{0,1})([0-9]*)([.]{0,1})([0-9]*)$/) = ["", "-", "1", ".", "2", ""];
		// parts[0] = unwanted stuff
		// parts[1] = the sign character
		// parts[2] = the whole part
		// parts[3] = the decimal character
		// parts[4] = the fractional part
		// parts[5] = unwanted stuff
		const parts = value.split(/([+-]{0,1})([0-9]*)([.]{0,1})([0-9]*)$/);

		// if there are not six parts, or there are unwanted parts,
		//    this is not and can not be a nubmer, it is invalid
		if (parts.length !== 6 || !!parts[0] || !!parts[5]) {
			return {
				rawValue: value,
				sign: undefined,
				whole: undefined,
				fraction: undefined,
				normalized: undefined,
				boundValue: undefined,
				infoMessage: stringResource.get('empty'),
			};
		}

		const sign = parts[1] === '-' ? '-' : '';
		// remove leading zeros, if there are no digits default to zero
		const whole = parts[2].split(/[0]*([0-9]+)/)[1] || '0';
		// remove trailing zeros, if there are no digits default to blank
		const fraction = parts[4].split(/([0-9]*[1-9])/)[1] || '';
		// only add a period if fraction is not blank
		const period = fraction.length ? '.' : '';
		const normalized = `${sign}${whole}${period}${fraction}`;

		// if there are digits(a whole or fractional part) it is a valid number
		if (!!parts[2] || !!parts[4]) {
			return {
				rawValue: value,
				sign,
				whole,
				fraction,
				normalized,
				// For defect: https://jsw.ibm.com/browse/BATOOLKIT-2249
				...this._valueBounded(value, normalized, props || this.props),
			};
		}

		// else it is a potential number
		return {
			rawValue: value,
			sign,
			whole,
			fraction,
			normalized,
			boundValue: undefined,
			infoMessage: stringResource.get('empty'),
		};
	};

	_onValueChange = newValue => {
		const { onValueChange } = this.props;
		const { boundValue, rawValue, normalized, infoMessage } = this._valueInfo(newValue);

		// if the input can be a number, accept it
		if (normalized !== undefined) {
			this.setState({
				displayValue: rawValue,
				infoMessage,
			});

			// if there is additional processing, call it
			onValueChange && onValueChange(boundValue, rawValue);
		}
	};

	_onValueAccept = newValue => {
		const { value: oldValue, onValueAccept } = this.props;
		const { boundValue: newBoundValue, rawValue: newRawValue } = this._valueInfo(newValue);
		const {
			boundValue: oldBoundValue,
			rawValue: oldRawValue,
			infoMessage: oldInfoMessage,
		} = this._valueInfo(this._fixupValue(oldValue));

		this.setState({
			displayValue: oldRawValue,
			infoMessage: oldInfoMessage,
		});

		// check if the old raw input does not match the new raw input
		//    if both old and new are valid non-empty values compare for match as numbers
		//       else compare for match as strings
		// this may result in a callback where the boundValue has not changed even though the raw value has

		if (
			oldBoundValue && oldRawValue.length && newBoundValue && newRawValue.length
				? Number(oldRawValue) !== Number(newRawValue)
				: oldRawValue !== newRawValue
		) {
			// if there is additional processing
			onValueAccept && onValueAccept(newBoundValue, newRawValue);
		}
	};

	render() {
		// extract props that are to be manipulated or not passed down
		const {
			// numberInput specific
			max,
			ltMax,
			gtMin,
			min,
			step,
			scale,
			validation,
			allowEmpty,
			// inherited props
			className,
			onChange,
			onBlur,
			validationErrorText,
			hasValidationError,
			// all the other props
			...others
		} = this.props;
		const { displayValue, infoMessage } = this.state;

		const classNames = cx(`${_CLASS}`);
		const localMessage = this._useOnValueCallbacks && infoMessage ? infoMessage : undefined;

		return (
			<BaseInput
				id="number-input"
				data-tid="ba-common-numberInput"
				{...sanitize(others, NumberInput)}
				{...getInheritedProps(others, BaseInput)}
				className={`${classNames} ${className}`}
				ref={this._ref}
				onChange={this._useOnValueCallbacks ? onChange : this._onChange}
				onBlur={this._useOnValueCallbacks ? onBlur : this._onBlur}
				onValueChange={this._useOnValueCallbacks ? this._onValueChange : undefined}
				onValueAccept={this._useOnValueCallbacks ? this._onValueAccept : undefined}
				onKeyDown={this._onKeyDown}
				value={displayValue}
				validationErrorText={localMessage || validationErrorText}
				hasValidationError={localMessage ? true : hasValidationError}
			>
				{step > 0 && (
					<SVGIcon
						aria-controls="number-input"
						aria-label="number-input__spin-up"
						data-tid="ba-common-numberInput__spin-up"
						focusable={false}
						size="small"
						iconId={chevronUp.id}
						className={cx(`${_CLASS}__spin`, 'up')}
						onMouseDown={e => this._spin(e, +1)}
					/>
				)}
				{step > 0 && (
					<SVGIcon
						aria-controls="number-input"
						aria-label="number-input__spin-down"
						data-tid="ba-common-numberInput__spin-down"
						focusable={false}
						size="small"
						iconId={chevronDown.id}
						className={cx(`${_CLASS}__spin`, 'down')}
						onMouseDown={e => this._spin(e, -1)}
					/>
				)}
			</BaseInput>
		);
	}
}
